// import { check } from '@business/api/auth';
// import { ItemId } from '@business/entities/library';
// import { PackageItem } from './../../../modules/timeMaterials/AddProductOverlay/AddProductOverlay';
import { CompactOrgResponse, OrgAddressResponse, OrgId } from '@business/entities/organization';
import { InvoiceDiscountData } from '@modules/common/InvoiceDiscount';
import { TransactionInvoiceResponse } from '../TransactionInvoice';
import { InvoiceSalesTaxData } from './../../../modules/common/InvoiceSalesTax/InvoiceSalesTax';
import { POAddressFormData } from './../../../modules/orders/EditPOPage/POForm';
import { CompactUserResponse, UserId } from './../account/UserResponse';
import { BaseResponse } from './../common/BaseResponse';
import { CompactContactResponse } from './../contact/ContactResponse';
import { SimpleOrgResponse } from './../organization/OrgResponse';
import { InvoiceSettingsResponse } from './../TransactionInvoice/TransactionInvoiceResponse';
import { TMStageId, TMStageResponse } from './TMStageResponse';
import { TMDiscountResponse, WorkOrderBillingSummary } from './TMDiscountResponse';
import {
  OrderTermResponse,
  OrderShippingOption,
  PurchaseOrderResponse,
  OrderVendorResponse,
  PurchaseOrderLineItem,
  OrderShipmentResponse,
  OrderProductResponse,
  LabourItemResponse,
} from '../order';
import { LabourTypeResponse } from '../labour-type';
import { TMBoardId } from './TimeMaterialBoardResponse';
import { FolderId } from '../file';
import { ProposalDiscountFormData, TmFeeFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { VeryCompactProposalResponse } from '../proposal';
import { CategoryResponse } from '../category';
import { CompactProviderResponse } from './TmServiceProviderResponse';
import { RangValue } from './TMItemCategoryResponse';
import { TMEstimateFeeResponse } from './TMEstimateResponse';
import { TimeMaterialSectionResponse, TimeMaterialUserResponse } from '@business/entities/tm/TimeMaterialSectionResponse';
import { ProjectServiceResponse, ProjectUserResponse } from '@business/entities/project';
import { TimeMaterialTemplateId } from '@business/entities/tm/TemplateResponse';
import { PackageProductLineItemFormData } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';
import { OrderPackageId } from '../order/OrderPackageResponse';
import { TmJobTypeResponse } from './TmJobTypeResponse';
import { CategoryOptionId } from '../category_option';

export type TimeMaterialId = string;

export enum TimeMaterialStatus {
  open = 'open',
  completed = 'completed',
  canceled = 'canceled',
  draft = 'draft',
}
export enum TimeMaterialShippingOption {
  ready = 'ready',
  complete = 'complete',
}

export type TMSectionId = number;
export interface TMSectionResponse {
  id: TMSectionId;
  title: string;
  menuOrder: number;
}

export enum TimeMaterialAlertStatus {
  success = 'success',
  warning = 'warning',
  danger = 'danger',
}

export interface CompactTimeMaterialResponse extends BaseResponse {
  id: TimeMaterialId;
  name: string;
  description: string;
  organization: CompactOrgResponse;
  // jobType: TmJobTypeResponse;
  jobType: ProjectServiceResponse;
  status: TimeMaterialStatus;
  stageId: TMStageId;
  stage: TMStageResponse;
  term: OrderTermResponse;
  orderNumber: string;
  total: number;
  subtotal: number;
  balance: number;
  deliveryDate?: number;
  shippedDate?: number;
  billingEmail: string;
  shippingMethod: string;
  shippingAccountNo: string;
  trackId: string;
  billingAddress: OrgAddressResponse;
  shippingAddress: OrgAddressResponse;
  contact: CompactContactResponse;
  salesReps: UserId[];
  salesRepsNames: string[];
  // Full sales-rep records (name/email/phone) — used to resolve the project-list "Sales Person" short codes.
  salePersons?: CompactUserResponse[];
  shippingOption: OrderShippingOption;
  customerPoNo: string;
  purchaseOrders: PurchaseOrderResponse[];
  canEdit: boolean;
  cc: string;
  bcc: string;
  isHide: boolean;
  pendingVendors: OrderVendorResponse[];
  totalTrackedHours: number;
  totalTrackedCost: number;
  sections: TMSectionResponse[];
  boardId: TMBoardId;
  boardName: string;
  wmsStatus: string;
  projectManager?: CompactUserResponse;
  users: string[];
  trackingTime: boolean;
  dueDate?: number;
  installDate?: number;
  startDate?: number;
  isFlagged: boolean;
  noActivityPast3Days: boolean;
  lastCommunication?: number;
  alertMessage: string;
  alertStatus: TimeMaterialAlertStatus;
  template: string | null;
  templateId: TimeMaterialTemplateId;
}

/** GET time-materials/search (CompactTimeMaterialWithOrgResource): a work order as a search list shows it. */
export type TimeMaterialSearchResponse = Pick<CompactTimeMaterialResponse, 'id' | 'orderNumber' | 'name' | 'organization' | 'status' | 'contact'>;

export type VeryCompactTimeMaterialResponse = Pick<
  CompactTimeMaterialResponse,
  | 'id'
  | 'boardId'
  | 'boardName'
  | 'orderNumber'
  | 'name'
  | 'organization'
  | 'stage'
  | 'purchaseOrders'
  | 'status'
  | 'created'
  | 'updated'
  | 'isFlagged'
  | 'installDate'
  | 'noActivityPast3Days'
  | 'lastCommunication'
  | 'total'
  | 'projectManager'
  | 'salesReps'
  | 'alertMessage'
  | 'alertStatus'
>;
export type VeryCompactWorkOrderResponse = Pick<
  CompactTimeMaterialResponse,
  'id' | 'name' | 'total' | 'balance' | 'created' | 'status' | 'billingAddress' | 'contact' | 'jobType'
>;

export type VVCompactTimeMaterialResponse = Pick<CompactTimeMaterialResponse, 'id' | 'name' | 'orderNumber'>;

export type BillTimeMaterialResponse = Pick<CompactTimeMaterialResponse, 'id' | 'name' | 'orderNumber'> & {
  orgId: OrgId;
  groups: Pick<EstimateGroupResponse, 'id' | 'name'>[];
};

export interface BoardTimeMaterialResponse extends CompactTimeMaterialResponse {
  warningAItems: PurchaseOrderLineItem[];
  warningSItems: PurchaseOrderLineItem[];
  shippedItems: PurchaseOrderLineItem[];
}

export interface TimeMaterialResponse extends CompactTimeMaterialResponse {
  estimate: OrderEstimateResponse;
  subProjects: SubProjectOrderEstimateResponse[];
  invoices: TransactionInvoiceResponse[];
  settings: InvoiceSettingsResponse;
  logs: TimeMaterialStageLogResponse[];
  shipments: OrderShipmentResponse[];
  requestedItems: string[];
  logo: string;
  folderId: FolderId;
  // fees: TmFeeFormData[];
  fees: TMFeeResponse[];
  proposal: VeryCompactProposalResponse;
  tmSections: TimeMaterialSectionResponse[];
  tmUsers?: TimeMaterialUserResponse[];
  /** Discounts are billed as separate invoice lines (Work Orders created after discount lines existed). */
  discountLines: boolean;
  discounts: TMDiscountResponse[];
  billingSummary: WorkOrderBillingSummary;
}

export type SubProjectOrderEstimateResponse = Pick<OrderEstimateResponse, 'id' | 'groups' | 'total' | 'subTotal' | 'totalFee'> & {
  title: string;
  description: string;
  isChecked: boolean;
};

export type TMFeeId = string;
export type TMFeeResponse = Omit<TMEstimateFeeResponse, 'id'> & {
  id: TMFeeId;
  adjustmentAmount: number;
};

export interface TimeMaterialStageLogResponse extends BaseResponse {
  id: string;
  stageId: TMStageId;
  timeMaterialId: TimeMaterialId;
  time: number;
  user: CompactUserResponse;
  contact: CompactContactResponse;
}

export enum EstimateApprovalAction {
  noCreateInvoice = 'noCreateInvoice',
  createSaveInvoice = 'createSaveInvoice',
  createSendInvoice = 'createSendInvoice',
}

export type OrderEstimateId = string;

export enum OrderEstimateStatus {
  accepted = 'approved',
  rejected = 'rejected',
  pending = 'pending',
  draft = 'draft',
  sent = 'sent',
}

export enum ProductStatus {
  pending = 'pending',
  ordered = 'ordered',
  partial = 'partial',
  partialInstalled = 'partial_installed',
  received = 'received',
  returned = 'returned',
  installed = 'installed',
  delivered = 'delivered',
}

export interface OrderEstimateResponse extends BaseResponse {
  id: OrderEstimateId;
  name: string;
  number: string;
  status: OrderEstimateStatus;
  reason: string;
  importId: string;
  docNumber: string;
  totalAmount: number;
  subTotal: number;
  total: number;
  coTotal: number;
  balance: number;
  memo: string;
  message: string;
  estimateDate: number;
  expirationDate: number;
  user: CompactUserResponse;
  // items: EstimateLineItem[];
  groups: EstimateGroupResponse[];
  discount: InvoiceDiscountData | ProposalDiscountFormData;
  salesTax: InvoiceSalesTaxData;

  profit: number;
  margin: number;
  partsCost: number;
  labourCost: number;
  partsPrice: number;
  labourPrice: number;
  totalCost: number;
  totalFee: number;
  // 'total'       => $this->total,
  labourHours: number;
  markup: number;
}
export type EstimateLineItemId = number;
export interface EstimateLineItem {
  id: EstimateLineItemId;
  productId: string;
  // provider: CompactProviderResponse;
  quantity: number;
  title: string;
  laborPrice: number;
  materialPrice: number;
  markupFactor: number;
  weProvide: boolean;
  description: string;
  total: number;
  thumbnail: string;
  isPrivate: boolean;
  isDeleted: boolean;
  product: Pick<OrderProductResponse, 'id' | 'title' | 'subcontractorWoDisplayPrice' | 'subcontractorWoDisplayQuantity'>;
}

export type EstimateGroupId = number;
export interface EstimateGroupResponse {
  id: EstimateGroupId;
  name: string;
  category: CategoryResponse;
  optionId: CategoryOptionId | null;
  provider: CompactProviderResponse | null;
  createdAt: number;
  hiPrice: number;
  loPrice: number;
  markupFactor: number;
  range: RangValue;
  items: EstimateLineItem[];

  total: number;
  subTotal: number;

  adjustmentAmt: number;
  coTotal: number;
  invoiceTotal: number;
  isEdit: boolean;
  taskChecklistStats: {
    total: number;
    completed: number;
    pending: number;
    isDue: boolean;
    taskId: string | null;
  };
}

export type CompactEstimateGroupResponse = Pick<EstimateGroupResponse, 'id' | 'name' | 'category'>;

export interface ExtendedEstimateLineItem extends EstimateLineItem {
  selectedQuantity: number;
}

export interface TmTrackingProductResponse {
  productId: number;
  quantity: number;
}
export interface EstimateCopyLineItem extends EstimateLineItem {
  lineCopyId: string;
  product: OrderProductResponse;
}

export interface OrderShippingRequest {
  deliveryDate?: number;
  shippedDate?: number;
  shippingMethod: string;
  shippingAccountNo: string;
  trackId: string;
}

export interface OrderEmailTemplateRequest {
  to?: string;
  cc?: string;
  bcc?: string;
  body?: string;
  isSave?: boolean;
  files?: File[];
}

export interface TimeMaterialRequest extends OrderEmailTemplateRequest {
  orgId: OrgId;
  billingEmail: string;
  billingAddress?: string | POAddressFormData;
  memo: string;
  message: string;
  status: string;
  poNumber?: string;
  shippingAddress?: string | POAddressFormData;
}

export interface OrderCancelRequest {
  reason: string;
  deleteEstimate?: boolean;
  deleteInvoice?: boolean;
  deletePurchaseOrder?: boolean;
}

export interface OrderWithItemsResposne {
  id: TimeMaterialId;
  title: string;
  items: EstimateCopyLineItem[];
}

export type TmPhaseId = string;
export interface CompactTimeMaterialPhaseResponse {
  id: string;
  title: string;
  menuOrder: number;
  estCompletionDate: number;
  timeMaterialId: TimeMaterialId;
}

export interface TimeMaterialPhaseResponse extends CompactTimeMaterialPhaseResponse {
  items: TimeMaterialPhaseItem[];
}

export interface TimeMaterialPhaseItem {
  id: string;
  lineItem: TimeMaterialPhaseLineItem;
  menuOrder: number;
}

export interface TimeMaterialPhaseLineItem {
  itemId: string;
  title: string;
  description: string;
  packageName: string;
  room: string;
  vendor: string;
  brand: string;
  qty: number;
  attachedQty: number;
  fulfilledQty: number;
  received: number;
  cost: number;
  price: number;
  lineTotal: number;
  status: ProductStatus;
  warehouseStatus: string;
  sku: string;
  installedQty: number;
  statusDate: number;
  isPrivate: boolean;
  isDeleted: boolean;
}

export interface TimeMaterialPhaseRequest {
  title: string;
  menuOrder: number;
  date: number;
}

export interface TmReportChartSeriesResponse {
  name: string;
  type: string;
  data: number[];
}

export interface CompletedLineResponse {
  checked: boolean;
  quantity: number;
  itemId: EstimateLineItemId;
}

export type ClientOrderResponse = Pick<
  TimeMaterialResponse,
  | 'id'
  | 'organization'
  | 'orderNumber'
  | 'name'
  | 'customerPoNo'
  | 'term'
  | 'projectManager'
  | 'salesRepsNames'
  | 'invoices'
  | 'billingEmail'
  | 'folderId'
  | 'total'
  | 'subtotal'
  | 'balance'
> & {
  totalFee: number; //Pick<OrderEstimateResponse, 'totalFee'>;
  totalAmount: number; //Pick<OrderEstimateResponse, 'totalAmount'>;
  coTotal: number; // Pick<OrderEstimateResponse, 'coTotal'>;
  salePersons: CompactUserResponse[];
};

export interface InvoiceRequest {
  id: string;
  amount: number;
}
export interface ClientUpgradeResponse extends SubProjectOrderEstimateResponse {
  initialApprove: boolean;
}
export interface ClientUpgradePreviewResponse extends ClientUpgradeResponse {
  acceptedBy: string;
  acceptedAt: string;
  workOrderNumber: string;
  workOrderName: string;
  organizationName: string;
}
